import { z } from 'zod';

import {
  automationOfferCheckResponseSchema,
  campaignOfferResponseSchema,
  leadFunnelResponseSchema,
  offerEditionsResponseSchema,
  offerResponseSchema,
  offersListResponseSchema,
  offerUsagesResponseSchema,
} from './contracts';
import { decodeAutomationOfferCheck, decodeCampaignOffer, decodeEditionStats, decodeOffer, decodeUsage } from './decoders';
import { requestContract } from './request';
import { offerFunnelRoutes as routes } from './routes';
import type { LeadFunnelDraft, LeadFunnelResult, OfferInput, OfferUsagesPage } from './types';

export const USAGES_PAGE_SIZE = 50;

export async function listOffers(artistId: string) {
  const body = await requestContract(offersListResponseSchema, 'get', routes.offers(artistId));
  return body.data.map(decodeOffer);
}

export async function saveOffer(artistId: string, input: OfferInput) {
  const data = {
    title: input.title,
    description: input.description,
    pdf_url: input.pdfUrl,
    cover_image_url: input.coverImageUrl,
    file_size: input.fileSize,
  };
  const url = input.id ? routes.offer(artistId, input.id) : routes.offers(artistId);
  const body = await requestContract(offerResponseSchema, input.id ? 'patch' : 'post', url, { data });
  return decodeOffer(body.data);
}

/** One bounded page. The panel never scans emails in the browser (C13). */
export async function getOfferUsages(artistId: string, offerId: string, cursor?: string | null) {
  const params = { limit: USAGES_PAGE_SIZE, ...(cursor ? { cursor } : {}) };
  const body = await requestContract(offerUsagesResponseSchema, 'get', routes.offerUsages(artistId, offerId), { params });
  const page: OfferUsagesPage = {
    items: body.data.map(decodeUsage),
    nextCursor: body.pagination.next_cursor,
    hasMore: body.pagination.has_more,
  };
  return page;
}

export async function getOfferEditions(artistId: string, offerId: string) {
  const body = await requestContract(offerEditionsResponseSchema, 'get', routes.offerEditions(artistId, offerId));
  return body.data.map(decodeEditionStats);
}

/** A new edition replaces the old one for new sign-ups; enrolled leads keep theirs (offers.replaces_offer_id). */
export async function replaceOfferPdf(artistId: string, offerId: string, file: { pdfUrl: string; fileSize: number }) {
  const data = { pdf_url: file.pdfUrl, file_size: file.fileSize };
  const body = await requestContract(offerResponseSchema, 'post', routes.offerEditions(artistId, offerId), { data });
  return decodeOffer(body.data);
}

export async function getCampaignOffer(artistId: string, campaignId: string) {
  const body = await requestContract(campaignOfferResponseSchema, 'get', routes.campaignOffer(artistId, campaignId));
  return decodeCampaignOffer(body.data);
}

/** Sends offer_id and nothing else: a full body is how the old staff save wiped offers. */
export async function changeCampaignOffer(artistId: string, campaignId: string, offerId: string) {
  await requestContract(z.unknown(), 'put', routes.leadCampaign(artistId, campaignId), { data: { offer_id: offerId } });
}

/** The one deliberate clear. The server answers 409 OFFER_IN_USE while a published automation sends it. */
export async function clearCampaignOffer(artistId: string, campaignId: string) {
  await requestContract(z.unknown(), 'put', routes.leadCampaign(artistId, campaignId), { data: { offer_id: null } });
}

export async function getAutomationOfferChecks(artistId: string, automationId: string) {
  const url = routes.automationOfferChecks(artistId, automationId);
  const body = await requestContract(automationOfferCheckResponseSchema, 'get', url);
  return decodeAutomationOfferCheck(body.data);
}

export async function publishLeadFunnel(artistId: string, draft: LeadFunnelDraft, idempotencyKey: string) {
  const data = {
    campaign: {
      name: draft.campaign.name,
      title: draft.campaign.title,
      subtitle: draft.campaign.subtitle,
      image_url: draft.campaign.imageUrl,
    },
    offer_id: draft.offerId,
    emails: draft.emails.map((email) => ({
      subject: email.subject,
      body_html: email.bodyHtml,
      delay_days: email.delayDays,
    })),
  };
  const headers = { 'Idempotency-Key': idempotencyKey };
  const body = await requestContract(leadFunnelResponseSchema, 'post', routes.leadFunnels(artistId), { data, headers });
  const result: LeadFunnelResult = {
    campaignId: body.data.campaign_id,
    automationId: body.data.automation_id,
    approvalStatus: body.data.approval_status,
  };
  return result;
}
